import React from "react";
import { graphql, useStaticQuery } from 'gatsby';

// Utils
import { useClass } from "@breezerr";
import { usePage } from "@brz-utils/contexts/page.context";
import { newLinesToBreaks } from "@brz-utils/newlines.to.breaks";

// Components
import { Img } from "@brz-components/elements/img";
import Logo from "@brz-components/elements/logo";
import { ButtonBase } from "@mui/material";
import { TextLink } from "@brz-components/elements/text.link";
import { TinaMarkdown } from 'tinacms/dist/rich-text';



// Types
import { FooterDataModel } from "@brz-types/footer.type";
import { useTinaMarkdownContent } from "@brz-utils/use.tina.markdown.content";



export const Footer = () => {
    const { page, template } = usePage();    

    if ( !page || !template ) {
        console.warn('A page trying to use the footer but the page context is not available.');
        
        return null;
    }

    const { footer } = useStaticQuery<FooterDataModel>(query);

    const data = footer.tina.data.node;

    const copyright = data.bottom.copyright.replace(/{{currentYear}}/g, new Date().getFullYear().toString());
    const disclaimer = useTinaMarkdownContent(page?.disclaimer?.content, data.disclaimer); 
    
    
    const [ready, setReady] = React.useState(false);

    React.useEffect(() => {
        setReady(true);
    }, []);

    return (
        <footer id="footer" className={useClass('w-100vw')} style={{ visibility: ready ? undefined : 'hidden' }}>
            {template?.disable?.footerContent || page.footer?.disableLinks ? null : <>
                {/* Columns  (Links) */}
                <div className={useClass('container md:d-grid md:grid-cols-2 lg:grid-cols-4 gap-x-3rem pt-9rem lg:pt-18rem text-center md:text-left')}>
                    {data.columns.map((column, index) => {
                        return <div key={index} className={useClass('md:d-flex lg:d-block flex-column justify-content-between')}>
                            <div className={useClass('mb-3rem lg:mb-7rem lg:h-17.4rem w-100% flex-column justify-content-center align-items-center')}>
                                {/* If the first column show address and logo*/}
                                {index === 0 ? (
                                    <div className={useClass('md:pl-4rem lg:pl-0rem md:mt-6rem lg:mt-0rem')}>
                                        <Logo disablePartner />
                                        <address className={useClass('mt-4.5rem mb-4.5rem lg:mb-0x text-size-1.4rem lh-2.5rem text-color-primary text-normal d-block__br')} dangerouslySetInnerHTML={{ __html: newLinesToBreaks(data.address) }}></address>
                                    </div>
                                ) : (
                                    <Img className={useClass('d-block max-w-100% sm:max-w-100% w-auto h-auto mx-auto md:mx-unset')} {...column.image} mode={'limit'} />
                                )}
                            </div>
                            <div className={useClass(`mb-6rem md:pl-4rem lg:pl-0rem`)}>
                                <div className={useClass('')}>
                                    <div className={useClass('text-size-1.7rem -ls-0.04rem text-800 mt-0x mb-1.5rem lg:mb-4.5rem')} dangerouslySetInnerHTML={{ __html: column.title }}></div>
                                    <div className={useClass('d-flex flex-column md:align-items-start align-items-center')}>
                                        {column.links.map((item, index) => {
                                            return <TextLink className={useClass('no-underline! hover:underline! text-size-1.4rem lh-3.2rem')} key={index} href={item.link}>{item.label}</TextLink>
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    })}
                </div>

                {/* Contacts + Extras */}
                <div className={useClass('container lg:d-grid grid-cols-4 gap-x-3rem mt-9rem lg:mt-6rem')}>

                    {/* Contacts */}
                    <div className={useClass('d-flex flex-column align-items-center lg:align-items-start mb-6rem lg:mb-3rem')}>
                        {data.contacts.map((item, index) => {
                            return <ButtonBase
                                key={index}
                                className={useClass(`d-flex align-items-center h-3rem ${index ? 'mt-0.8rem' : ''} no-underline -ml-1rem lg:ml-0x radius-1.5rem pr-1.5rem hover:bg-color-grey:50`)}
                                // component={TextLink}
                                href={item.link}
                            >
                                <Img className={useClass('radius-100% bg-color-grey w-3rem! h-3rem! mr-1rem')} src={item.icon} width={30} height={30} />
                                <span className={useClass('text-size-1.3rem text-500')}>{item.label}</span>
                            </ButtonBase>
                        })}
                    </div>

                    {/* Extras */}
                    {data.extras.map((item, index) => {
                        const column = item;
                        return <div key={index} className={useClass(`${index === 1 ? 'col-span-1' : 'col-span-2'} mb-3rem`)}>
                            <div className={useClass('text-size-1.4rem text-800 lh-3rem -ls-0.03rem mt-0x mb-1rem text-center lg:text-left')} dangerouslySetInnerHTML={{ __html: item.title }}></div>
                            <div className={useClass('d-flex flex-wrap justify-content-center lg:justify-content-start -ml-0.8rem')}>
                                {item.links.map((item, index) => {
                                    return <ButtonBase
                                        key={index}
                                        className={useClass(`ws-nowrap h-2.4rem bg-color-grey hover:bg-color-grey:300 text-color-primary! radius-1.2rem text-size-1.2rem text-500 lh-1rem px-1.5rem mb-1rem mx-0.5rem`)}
                                        href={item.link}
                                        component={TextLink}
                                    >{item.label}</ButtonBase>
                                })}
                            </div>
                        </div>
                    })}
                </div>
            </>}

            {/* Bottom Bar */}
            {page.footer?.disableBottom ? null : <>
                <div className={useClass('w-100% bg-color-secondary:100 py-6rem mt-6rem lg:mt-9rem')}>
                    <div className={useClass('container d-grid lg:grid-cols-12 lg:gap-x-3rem justify-content-center lg:justify-content-start')}>
                        <div className={useClass('d-flex align-items-center order-3 lg:order-1 lg:col-span-5 text-color-primary text-size-1.2rem text-center lg:text-left')} dangerouslySetInnerHTML={{ __html: copyright }}></div>

                        <div className={useClass('order-2 lg:col-span-2 d-flex justify-content-center mb-3rem lg:mb-0x')}>{
                            data.bottom.social.map((item, index) => {
                                return <a className={useClass('d-flex w-32px h-32px align-items-center justify-content-center')} href={item.link} title={item.label} key={index}>
                                    <Img className={useClass('d-block')} src={item.icon} />
                                </a>
                            })
                        }</div>

                        <div className={useClass('order-1 lg:order-3 lg:col-span-5 text-size-1.2rem text-center lg:text-right d-flex flex-column lg:flex-row justify-content-end align-items-center lg:align-items-center mb-3rem lg:mb-0x')}>
                            {data.bottom.navigation ? (
                                data.bottom.navigation.map((item, index) => {
                                    return <TextLink className={useClass('mb-1.5rem lg:mb-0x mx-0x lg:ml-1.5rem no-underline! hover:underline! px-1.5rem lg:px-0x')} key={index} href={item.link} target={item.newWindow ? '_blank' : '_self'}>{item.label}</TextLink>
                                })
                            ) : ('')}
                            <span className={useClass('d-none lg:d-inline mx-1.5rem')}>|</span>
                            <span className={useClass('text-color-primary')}>
                                Sitemap: <a className={useClass('no-underline! hover:underline! text-color-primary')} href="/sitemap-index.xml">XML</a><TextLink className={useClass('no-underline! hover:underline! ml-0.5rem')} href="/sitemap/">HTML</TextLink>
                            </span>
                        </div>
                    </div>
                </div>
            </>}

            {/* Disclaimer */}
            {page?.disclaimer?.disable === true || !disclaimer ? null : (
                <aside id="disclaimer" className={useClass(`w-100vw bg-color-secondary:100 py-4x border-top-1px-solid border-color-secondary ${page?.footer?.disableBottom ? 'mt-8rem lg:mt-12rem' : ''}`)}>
                    <div className={useClass('container lg:px-1x text-color-primary lh-2rem text-size-1.2rem ls-0.03rem')}>
                        <TinaMarkdown content={disclaimer} />
                    </div>
                </aside>
            )}
        </footer>
    );
};


const query = graphql`
    query TinaFooterQuery {
        footer: tinaNode {
            tina(
                queryFilePath: "src/components/footer/footer.gql", 
                variables: { nodeId: "content\\settings\\footer.json" }
            )
        }
    }
`